import React, { useState } from 'react'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { object, string } from 'yup'
import isEmpty from 'lodash/isEmpty'
import { useQueryClient } from '@tanstack/react-query'

import { useDialog } from '@/hooks'
import { Dialog, InfoBox } from '@/components'
import { B2CTransferRequestForm } from '@/@types/b2c.types.ts'
import { useGetSbpParticipants, useMutateB2cTransferCheck } from '@/services'
import { preventHandler } from '@/utils.ts'

import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  SubmitButton,
  SubmitErrorMessage,
  TextFieldControl,
} from '../../../../components/form'

import { SbpParticipantsField } from './sbp-participants-field.tsx'
import { B2CConfirmDialog } from './b2c-confirm-dialog.tsx'
import { useUser } from '@/stores/slices/user-slice.ts'

const b2cTransferValidationSchema = object<FormSchema<B2CTransferRequestForm>>({
  rcvPhone: string().required(FormSchemaMessages.required).test(FormSchemaTests.isPhoneNumber),
  amount: string().required(FormSchemaMessages.required).test(FormSchemaTests.isWithinRange(10, 1000000)),
  rcvFio: string(),
  reference: string(),
  remitInfo: string().required(FormSchemaMessages.required),
  rcvBankMemberId: string().required(FormSchemaMessages.required),
})

const B2C_TRANSFER_FORM_NAME = 'newB2CTransferCheck'
export const NewB2CTransferDialog = (): React.ReactElement => {
  const [stage, setStage] = useState<'check' | 'confirm'>('check')
  const qc = useQueryClient()

  const user = useUser()
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()

  // предварительно загружаем и кешируем список банков для перевода
  useGetSbpParticipants()

  const { reset, error, data, isPending, isError, mutateAsync } = useMutateB2cTransferCheck()
  const handleFormSubmit = async (formValues: B2CTransferRequestForm) => {
    try {
      await preventHandler(mutateAsync, isPending)(formValues)
      setStage('confirm')
    } catch (e) {
      console.log()
    }
  }

  const handeCloseDialog = () => {
    qc.invalidateQueries({ queryKey: ['b2c_list'] })
    handleHideDialog()
    reset()
  }

  const isConfirmationStage = stage === 'confirm' && !isEmpty(data?.data)
  return (
    <Box>
      <Button color="primary" onClick={handleShowDialog}>
        Создать B2C перевод
      </Button>
      {showDialog && (
        <Form name={B2C_TRANSFER_FORM_NAME} validationSchema={b2cTransferValidationSchema} onSubmit={handleFormSubmit}>
          <Dialog
            title="Заявка на новый B2C перевод"
            open={showDialog}
            onClose={handeCloseDialog}
            maxWidth="md"
            content={
              isConfirmationStage ? (
                <B2CConfirmDialog {...data?.data} onSuccess={handeCloseDialog} />
              ) : (
                <Stack gap={3}>
                  <Stack gap={2} direction="column">
                    <InfoBox severity="success">
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="body2" fontWeight="bold">
                          Счёт списания
                        </Typography>
                        <Typography variant="body1">{user?.partner.accountNumber}</Typography>
                      </Stack>
                    </InfoBox>
                    <TextFieldControl<B2CTransferRequestForm>
                      name="rcvPhone"
                      label="Номер телефона получателя"
                      helperText="Без кода страны"
                      fullWidth
                      normalize={InputNormalizers.digitsOnly}
                    />
                    <TextFieldControl<B2CTransferRequestForm>
                      name="amount"
                      fullWidth
                      normalize={InputNormalizers.positiveNumbersOnly()}
                      label="Сумма платежа"
                    />
                    <TextFieldControl<B2CTransferRequestForm> name="rcvFio" label="Ф.И.О. получателя" fullWidth />
                    <SbpParticipantsField />
                    <TextFieldControl<B2CTransferRequestForm> name="reference" label="№ заявки B2C" fullWidth />
                    <TextFieldControl<B2CTransferRequestForm>
                      name="remitInfo"
                      label="Назначение платежа В2С"
                      fullWidth
                    />
                  </Stack>
                  <Grid direction="column" spacing={1} container>
                    <Grid>
                      <SubmitErrorMessage
                        message={
                          isError
                            ? error?.response?.data?.errorMessage
                            : 'Произошла непредвиденная ошибка, попробуйте ещё раз'
                        }
                        isError={isError}
                      />
                    </Grid>
                    <Grid>
                      <SubmitButton form={B2C_TRANSFER_FORM_NAME}>
                        {isConfirmationStage ? 'Продолжить' : 'Подтвердить'}
                      </SubmitButton>
                    </Grid>
                  </Grid>
                </Stack>
              )
            }
          />
        </Form>
      )}
    </Box>
  )
}
